import { html, nothing } from 'lit';
import { highlightStateProvider, seriesType } from '../types.js';
import { legendEventCallbacks, legendsConfiguration } from './types.js';
import { styleMap } from 'lit/directives/style-map.js';
import { defaultOpacityOnInactiveHighlight } from '../style.js';
import { ref } from 'lit/directives/ref.js';
export const Legend = ({
  legendsConfiguration,
  type,
  color,
  name,
  value,
  highlightStateProvider,
  onLegendMouseEnter,
  onLegendMouseLeave
}: {
  legendsConfiguration: legendsConfiguration;
  type: seriesType;
  color: string;
  name: string;
  value?: string;
  highlightStateProvider: highlightStateProvider;
} & legendEventCallbacks) => {
  const {
    legendName,
    legendIcon,
    legendsColumnCount,
    legendsOffsetLeft
  } = legendsConfiguration;
  const opacity = highlightStateProvider(name) === 'inactive' ? defaultOpacityOnInactiveHighlight : undefined;
  return html`
    <div
      style=${styleMap({
    display: 'flex',
    'align-items': 'flex-start',
    'justify-content': 'space-between',
    overflow: 'hidden',
    width: legendsColumnCount > 0 ? `calc((100% / ${legendsColumnCount}) - ${legendsOffsetLeft}px)` : undefined
  })}
    >
      <div
        style=${styleMap({
    display: 'flex',
    'align-items': 'flex-start',
    'max-width': '100%',
    cursor: 'default',
    opacity
  })}
        @mouseenter=${() => {
    if (onLegendMouseEnter) onLegendMouseEnter(name);
  }}
        @mouseleave=${onLegendMouseLeave}
      >
        ${LegendIcon(legendsConfiguration, type, color)}
        <span
          ${ref(($ref?: Element) => handleTooltip(name, $ref))}
          class="one-ux-typography--${legendName.typography}"
          style=${styleMap({
    margin: '0',
    'margin-left': `${legendName.offsetLeft}px`,
    color: 'var(--one-ux-palette--brand-neutral-600)',
    'white-space': legendName.whiteSpace,
    'line-height': `${legendIcon.height}px`,
    overflow: 'hidden',
    'text-overflow': 'ellipsis',
    'max-width': legendName.maxWidth ? `${legendName.maxWidth}px` : undefined,
    'word-wrap': 'break-word'
  })}
        >
          ${name}${value ? ':' : nothing}</span
        >
      </div>
      ${value ? html`<span
            class="one-ux-typography--mono-300"
            style=${styleMap({
    margin: 0,
    'margin-left': '2px',
    'margin-top': `${getMarginTopForValue()}px`,
    color: 'var(--one-ux-palette--brand-neutral-600)',
    'font-weight': 'bold',
    'line-height': `${legendIcon.height}px`
  })}
          >
            ${value}
          </span>` : nothing}
    </div>
  `;
};
const LegendIcon = (configuration: legendsConfiguration, type: seriesType, color: string) => {
  const legendIcon = configuration.legendIcon;
  switch (type) {
    case 'column':
    case 'column-overlapped':
    case 'column-stacked':
      return html`<div
        style=${styleMap({
        width: `${legendIcon.width}px`,
        'min-width': `${legendIcon.width}px`,
        height: `${legendIcon.height}px`,
        background: color,
        display: 'inline-flex'
      })}
      ></div>`;
    case 'line':
      return html`<div
        style=${styleMap({
        width: `${legendIcon.width}px`,
        'min-width': `${legendIcon.width}px`,
        height: `${legendIcon.height}px`,
        display: 'inline-flex',
        'justify-content': 'center',
        'align-items': 'center'
      })}
      >
        <div
          style=${styleMap({
        width: '100%',
        height: '2px',
        background: color,
        display: 'inline-flex',
        'justify-content': 'center',
        'align-items': 'center'
      })}
        >
          <div
            style=${styleMap({
        width: '4px',
        height: '4px',
        background: color,
        margin: '0 auto',
        'border-radius': '100%'
      })}
          ></div>
        </div>
      </div>`;
    case 'area':
      return html`<div
        style=${styleMap({
        width: `${legendIcon.width}px`,
        'min-width': `${legendIcon.width}px`,
        height: `${legendIcon.height}px`,
        position: 'relative',
        display: 'inline-flex'
      })}
      >
        <div
          style=${styleMap({
        width: '100%',
        height: '2px',
        background: color,
        display: 'inline-flex',
        'justify-content': 'center',
        'align-items': 'center'
      })}
        ></div>
        <div
          style=${styleMap({
        width: '100%',
        height: '100%',
        background: color,
        opacity: '0.3',
        position: 'absolute'
      })}
        ></div>
      </div>`;
    default:
      return nothing;
  }
};
const getMarginTopForValue = () => {
  if (window.devicePixelRatio - Math.floor(window.devicePixelRatio) === 0.5) return -0.5;
  return Math.floor(window.devicePixelRatio / 0.25 % 2) === 0 ? -1 : 0;
};
const handleTooltip = (name: string, $ref?: Element) => {
  if (!$ref) return;
  requestAnimationFrame(() => {
    const $text = $ref as HTMLElement;
    const $parent = $text.parentElement!;
    const isTruncatedText = $text.offsetWidth < $text.scrollWidth;
    if (isTruncatedText) {
      $parent.setAttribute('one-ux-tooltip', name);
    } else {
      $parent.removeAttribute('one-ux-tooltip');
    }
    $parent.toggleAttribute('one-ux-tooltip-fixed', isTruncatedText);
    $parent.toggleAttribute('one-ux-tooltip-custom-aria', isTruncatedText);
  });
};